export const COMPLAINT_TYPE_STORAGE_KEY = 'aira-query-complaintTypeId';
export const STATUS_FIELD = { id: 'status', label: 'Status', key: 'status' };
export const ESCALATION_FIELD = { id: 'escalationTo', label: 'Escalation To', key: 'escalationTo' };
export const SUBMITTED_BY_FIELD = { id: 'submittedByUserName', label: 'Submitted By', key: 'submittedByUserName' };
export const UPDATED_AT_FIELD = { id: 'updatedAt', label: 'Updated At', key: 'updatedAt' };
export const UPDATED_AT_SORT_NEWEST = 'desc';
export const UPDATED_AT_SORT_OLDEST = 'asc';
export const DEFAULT_COMPLAINTS_PAGE_SIZE = 10;
export const COMPLAINTS_PAGE_SIZE_OPTIONS = ['5', '10', '15', '20', '25'];
export const REPORT_ROW_LIMIT = 999999;
export const RESERVED_TABLE_KEYS = new Set(['status', 'escalationTo', 'id', 'submittedByUserName', 'updatedAt']);
const MONTHS = [
  'January', 'Feb', 'Mar', 'Apr', 'May', 'June',
  'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec',
];

export function formatComplaintDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${hours}:${minutes}`;
}

function parseUpdatedAtTime(value) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function sortComplaintRowsByUpdatedAt(rows, sortDirection) {
  if (sortDirection !== UPDATED_AT_SORT_NEWEST && sortDirection !== UPDATED_AT_SORT_OLDEST) return rows;
  return [...rows].sort((left, right) => {
    const leftTime = parseUpdatedAtTime(left.updatedAtValue);
    const rightTime = parseUpdatedAtTime(right.updatedAtValue);
    if (leftTime === rightTime) return 0;
    return sortDirection === UPDATED_AT_SORT_OLDEST ? leftTime - rightTime : rightTime - leftTime;
  });
}

export function getNextUpdatedAtSortDirection(currentDirection) {
  return currentDirection === UPDATED_AT_SORT_NEWEST ? UPDATED_AT_SORT_OLDEST : UPDATED_AT_SORT_NEWEST;
}

export function buildComplaintTableFields(formFieldColumns) {
  return [SUBMITTED_BY_FIELD, UPDATED_AT_FIELD, ...formFieldColumns, STATUS_FIELD, ESCALATION_FIELD];
}

export function formFieldColumnsFromResponse(response) {
  const body = response?.data ?? response;
  const fields = Array.isArray(body) ? body : body?.data ?? body?.results ?? body?.items ?? [];
  return [...(Array.isArray(fields) ? fields : [])]
    .sort((left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0))
    .map((field) => {
      const key = field.fieldKey || field.fieldName || field.fieldLabel;
      return {
        id: key,
        label: field.fieldName || field.fieldLabel || field.label || key,
        key,
        fieldType: field.fieldType,
        hideForWorkflowStepId: field.hideForWorkflowStepId ?? null,
      };
    })
    .filter((field) => field.key && !RESERVED_TABLE_KEYS.has(field.key));
}

export function getPaginationInfo(meta, page, pageSize, rowCount) {
  const source = meta?.data ?? meta ?? {};
  const total = Number(source.total ?? source.totalItems ?? source.totalCount ?? source.count);
  const totalPages = Number(source.totalPages ?? source.pages ?? source.pageCount);
  if (Number.isFinite(totalPages) && totalPages > 0) {
    return {
      totalPages,
      totalItems: Number.isFinite(total) && total >= 0 ? total : Math.max(rowCount, totalPages * pageSize),
      hasNext: page < totalPages,
    };
  }
  if (Number.isFinite(total) && total >= 0) {
    return {
      totalPages: Math.max(1, Math.ceil(total / pageSize)),
      totalItems: total,
      hasNext: page * pageSize < total,
    };
  }
  return {
    totalPages: page + (rowCount >= pageSize ? 1 : 0),
    totalItems: Math.max(rowCount, page * pageSize),
    hasNext: rowCount >= pageSize,
  };
}

export function getComplaintTypeId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value.data ?? value.id ?? value._id ?? null;
  return value;
}

export function readStoredComplaintTypeId() {
  try {
    return sessionStorage.getItem(COMPLAINT_TYPE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function saveComplaintTypeId(value) {
  const id = getComplaintTypeId(value);
  if (id == null || id === '') return null;
  const normalized = String(id);
  try {
    sessionStorage.setItem(COMPLAINT_TYPE_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage failures.
  }
  return normalized;
}

export function getFirstAvailableComplaintTypeId(...values) {
  for (const value of values) {
    const resolved = getComplaintTypeId(value);
    if (resolved != null && resolved !== '') return String(resolved);
  }
  return null;
}

export function getComplaintStatusName(complaint, statusList = []) {
  const raw = complaint?.status;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (raw && typeof raw === 'object') {
    const name = raw.name ?? raw.label ?? raw.status;
    if (name) return String(name).trim();
    const nestedId = raw.id ?? raw.statusId;
    if (nestedId != null) {
      const match = statusList.find((item) => String(item.id) === String(nestedId));
      if (match?.name) return match.name;
    }
  }
  const statusId = complaint?.statusId ?? complaint?.complaintStatusId;
  if (statusId != null) {
    const match = statusList.find((item) => String(item.id) === String(statusId));
    if (match?.name) return match.name;
  }
  return '';
}

export function getComplaintStatusId(complaint, statusList = []) {
  const direct = complaint?.statusId ?? complaint?.complaintStatusId;
  if (direct != null && direct !== '') return direct;
  const raw = complaint?.status;
  if (raw && typeof raw === 'object') {
    const nestedId = raw.id ?? raw.statusId;
    if (nestedId != null && nestedId !== '') return nestedId;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const match = statusList.find((item) => String(item.name).trim() === raw.trim());
    if (match?.id != null) return match.id;
  }
  return null;
}

export function isEnabledFlag(value) {
  return value === true || value === 'Yes' || value === 'true' || value === 1 || value === '1';
}

export function isResolvedOrClosedStatus(name) {
  const key = String(name || '').trim().toLowerCase();
  return key === 'resolved' || key === 'closed';
}

export function getCurrentWorkflowStep(complaint, steps = []) {
  const stepId = complaint?.currentStepId ?? complaint?.workflowStepId ?? complaint?.currentWorkflowStepId;
  if (stepId != null && stepId !== '') {
    const match = steps.find((step) => String(step.id) === String(stepId));
    if (match) return match;
  }
  const level = complaint?.level ?? complaint?.levelNo;
  if (level != null && level !== '') return steps.find((step) => String(step.levelNo) === String(level)) ?? null;
  return null;
}

export function getEscalationLevelLabel(complaint, steps = []) {
  const level = complaint?.level ?? complaint?.levelNo;
  if (level != null && level !== '') return `Level ${level}`;
  const stepId = complaint?.workflowStepId ?? complaint?.currentWorkflowStepId ?? complaint?.currentStepId;
  if (stepId != null) {
    const match = steps.find((step) => String(step.id) === String(stepId));
    if (match?.levelNo != null) return `Level ${match.levelNo}`;
  }
  return '';
}

export function mapComplaintToTableRow(complaint, statusList = [], steps = []) {
  const currentStep = getCurrentWorkflowStep(complaint, steps);
  const row = {
    id: complaint?.complaintId ?? complaint?.id,
    status: getComplaintStatusName(complaint, statusList),
    statusId: getComplaintStatusId(complaint, statusList),
    escalationTo: getEscalationLevelLabel(complaint, steps),
    currentStepId: currentStep?.id ?? complaint?.currentStepId ?? complaint?.workflowStepId ?? complaint?.currentWorkflowStepId ?? null,
    canResolve: isEnabledFlag(complaint?.canResolve ?? currentStep?.canResolve),
    isFinalLevel: isEnabledFlag(complaint?.isFinalLevel ?? currentStep?.isFinalLevel ?? currentStep?.finalLevel),
    submittedByUserName: complaint?.submittedByUserName || '-',
    updatedAt: formatComplaintDateTime(complaint?.updatedAt) || '-',
    updatedAtValue: complaint?.updatedAt ?? '',
  };
  (complaint?.formField || []).forEach((field) => {
    const key = field.fieldKey || field.fieldName;
    if (key && !RESERVED_TABLE_KEYS.has(key)) row[key] = field.value;
  });
  return row;
}

export function buildComplaintColumns(rows, configuredFieldColumns) {
  const seen = new Set();
  const columns = configuredFieldColumns.map((field) => {
    seen.add(field.key);
    if (field.fieldType) return field;
    const sample = rows.flatMap((row) => row._fields || []).find((rowField) => (rowField.fieldKey || rowField.fieldName) === field.key);
    return sample?.fieldType ? { ...field, fieldType: sample.fieldType } : field;
  });
  rows.forEach((row) => {
    (row._fields || []).forEach((field) => {
      const key = field.fieldKey || field.fieldName;
      if (!key || RESERVED_TABLE_KEYS.has(key) || seen.has(key)) return;
      seen.add(key);
      columns.push({ id: key, label: field.fieldName || key, key, fieldType: field.fieldType });
    });
  });
  return columns;
}

export function isAttachmentColumn(field, rows, resolveIsAttachmentField) {
  const fieldType = String(field?.fieldType || '').toLowerCase();
  if (fieldType === 'file' || fieldType === 'images' || fieldType === 'image') return true;
  return rows.some((row) => resolveIsAttachmentField(field, row[field.key] ?? '-'));
}

export function unwrapComplaintListResponse(payload) {
  const body = payload?.data ?? payload;
  if (Array.isArray(body)) return body;
  return body?.data ?? body?.results ?? body?.items ?? [];
}

export function escapeCsvValue(value) {
  const str = value == null ? '' : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

export function buildCsvContent(columns, rows) {
  const header = columns.map((field) => escapeCsvValue(field.label)).join(',');
  const lines = rows.map((row) => columns.map((field) => escapeCsvValue(row[field.key] ?? '-')).join(','));
  return [header, ...lines].join('\r\n');
}
